import _ from 'lodash';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/libs/axios';
import { StatusUser } from '@/utils/constants/users';
import { fileSchemaOptional } from '@/utils/libs/schemaUtil';

export const userSchema = z.object({
  id: z.string().optional(),
  first_name: z.string().max(255).min(1, 'Required'),
  last_name: z.string().max(255).min(1, 'Required'),
  full_name: z.string().optional(),
  email: z.string().email().max(255),
  status_user: z.preprocess(
    value => value === StatusUser.valueOf(StatusUser.ACTIVE),
    z.boolean(),
  ),
  user_type: z.string(),
  medias: fileSchemaOptional,
});

export const userPasswordSchema = z.object({
  password: z.string().min(8),
});

export type IUserSummary = {
  total: number;
  active: number;
  inactive: number;
};

export type IUserSchema = z.infer<typeof userSchema>;

// for CreateEditUser form
export interface IUserSchemaExtended extends Omit<IUserSchema, 'status_user'> {
  status_user: string;
}

export type IUserPasswordSchema = z.infer<typeof userPasswordSchema>;

export const UserService = {
  useGet: (id?: string) =>
    useQuery<IUserSchemaExtended>({
      queryKey: ['user', id],
      queryFn: async () => {
        if (!id) {
          return undefined;
        }

        const res = await axiosInstance.get(`/api/crud/user/${id}`);
        const data = res.data.data;

        return {
          ...data,
          status_user: data.status_user
            ? StatusUser.valueOf(StatusUser.ACTIVE)
            : StatusUser.valueOf(StatusUser.INACTIVE),
        };
      },
    }),

  useGetSummary: () =>
    useQuery({
      queryKey: ['user-summary'],
      queryFn: async () =>
        (await axiosInstance.get('/api/crud/user/summary')).data,
    }),

  useGetAllUser: () =>
    useQuery({
      queryKey: ['all-user'],
      queryFn: async () => (await axiosInstance.get('/api/crud/user')).data,
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IUserSchemaExtended) => {
        const response = await axiosInstance.post(
          '/api/crud/user',
          {
            ...data,
            status_user:
              data.status_user === StatusUser.valueOf(StatusUser.ACTIVE),
          },
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        return response.data;
      },
    }),

  useUpdate: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (data: IUserSchemaExtended) => {
        const response = await axiosInstance.post(
          `/api/crud/user/${data.id}`,
          {
            ...data,
            status_user: data.status_user,
          },
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user'] });
      },
    });
  },

  useUpdatePassword: () =>
    useMutation({
      mutationFn: async ({
        user,
        password,
      }: {
        user: IUserSchema;
        password: IUserPasswordSchema;
      }) => {
        const response = await axiosInstance.post(`/api/crud/user/${user.id}`, {
          ..._.pick(user, [
            'email',
            'first_name',
            'last_name',
            'status_user',
            'user_type',
          ]),
          ...password,
        });

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(`/api/crud/user/${id}`);

        return response.data;
      },
    }),

  useGetProjectAssignees: (options?: { enabled?: boolean }) =>
    useQuery({
      queryKey: ['user-project-assignees'],
      queryFn: async () => {
        const res = await axiosInstance.get('/api/crud/user/my');
        return res.data.data;
      },
      enabled: options?.enabled ?? true,
    }),
};
